import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { Trash2, Mail, AlertTriangle, CheckCircle, Clock, Database, Shield } from 'lucide-react';
import { api } from '@/lib/api/client';

const deletionRequestSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  accountEmail: z.string().email('Please enter a valid email address').optional(),
  reason: z.enum(['privacy', 'no_longer_using', 'technical_issues', 'other', 'prefer_not_to_say']).optional(),
  additionalInfo: z.string().max(500, 'Additional information must be less than 500 characters').optional(),
});

type DeletionRequestFormValues = z.infer<typeof deletionRequestSchema>;

const REASONS = [
  { value: 'privacy', label: 'Privacy concerns' },
  { value: 'no_longer_using', label: 'No longer using the platform' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export default function AccountDeletionRequestPage() {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<DeletionRequestFormValues>({
    resolver: zodResolver(deletionRequestSchema),
    defaultValues: {
      name: '',
      email: '',
      accountEmail: '',
      reason: 'prefer_not_to_say',
      additionalInfo: '',
    },
  });

  const { isSubmitting } = form.formState;

  async function onSubmit(values: DeletionRequestFormValues) {
    const subject = encodeURIComponent('Account Deletion Request');
    const body = encodeURIComponent(
      `Account Deletion Request

Name: ${values.name}
Email: ${values.email}
Account Email: ${values.accountEmail || values.email}
Reason: ${values.reason || 'Not specified'}

Additional Information:
${values.additionalInfo || 'None'}

---
This request was submitted via the account deletion request form on ${new Date().toLocaleDateString()}.
`
    );

    const response = await api.post('/api/account-deletion-request', {
      name: values.name,
      email: values.email,
      accountEmail: values.accountEmail || values.email,
      reason: values.reason,
      additionalInfo: values.additionalInfo,
    });

    if (response.success) {
      setSubmitted(true);
      toast.success('Deletion request submitted through platform. Check your email for confirmation.');
    } else {
      const errorMessage = response.error?.message || 'Failed to submit deletion request';
      const errorCode = response.error?.code;

      if (errorCode === 'VALIDATION_ERROR') {
        toast.error('Validation failed', { description: errorMessage });
      } else {
        window.location.href = `mailto:streaming@dominioncity.org.za?subject=${subject}&body=${body}`;
        setSubmitted(true);
        toast.success('Opening your email client to send the deletion request.');
      }
    }
  }

  return (
    <div className="@container/deletion max-w-4xl mx-auto w-full px-3 py-4 @[740px]/deletion:px-4 @[740px]/deletion:py-5 @[1100px]/deletion:px-6 @[1100px]/deletion:py-6 space-y-6 @[740px]/deletion:space-y-7 @[1100px]/deletion:space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-3 @[740px]/deletion:space-y-4">
        <Badge variant="outline" className="mb-2">
          <Trash2 className="mr-1 h-3 w-3" />
          Account Deletion
        </Badge>
        <h1 className="text-4xl font-bold">Request Account Deletion</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Submit a request to delete your Dominion Mandate account and all associated data.
        </p>
      </div>

      {/* What Happens Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <Clock className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-base">30-Day Grace Period</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Your account will be scheduled for deletion and can be recovered within 30 days.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <Database className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-base">Complete Data Removal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              All your personal data, streams, comments, and activity will be permanently deleted.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <Shield className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-base">Secure Process</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We'll verify your identity via email before processing the deletion request.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <Mail className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-base">Email Confirmation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              You'll receive a confirmation email with details about the deletion process.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Important Notice */}
      <Card className="border-destructive/50 bg-destructive/5">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-lg">Important Notice</CardTitle>
          </div>
          <CardDescription>
            Please read carefully before proceeding with account deletion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
            <li>After the 30-day grace period, your account cannot be recovered</li>
            <li>All your streams, recordings, and content will be permanently deleted</li>
            <li>Your comments and interactions will be removed</li>
            <li>You will lose access to all invite-only streams</li>
            <li>If you want to use the platform again, you'll need to create a new account</li>
          </ul>
        </CardContent>
      </Card>

      {/* Deletion Request Form */}
      {submitted ? (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <CardTitle>Request Submitted</CardTitle>
            </div>
             <CardDescription>
               Thank you for submitting your account deletion request
             </CardDescription>
           </CardHeader>
           <CardContent className="space-y-4">
             <p className="text-sm text-muted-foreground">
               Your deletion request has been received. We will send a confirmation email shortly with
               next steps. If you don't see the email within a few minutes, please check your spam folder.
             </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => window.location.href = '/'}>
                Return Home
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/help'}>
                Visit Help Center
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="max-w-2xl mx-auto">
           <CardHeader>
             <CardTitle>Submit Deletion Request</CardTitle>
             <CardDescription>
               Fill out the form below to request account deletion. Your request will be securely submitted through the platform.
             </CardDescription>
           </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="your@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="accountEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Email (if different)</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="account@email.com"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">
                        If the email for your account is different from your contact email, enter it here.
                      </p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason for Deletion (Optional)</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        >
                          {REASONS.map((reason) => (
                            <option key={reason.value} value={reason.value}>
                              {reason.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="additionalInfo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Information (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Is there anything specific you'd like us to know? (Max 500 characters)"
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Submit Deletion Request
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  By submitting this request, you acknowledge that you understand the consequences
                  of account deletion as described above.
                </p>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Alternative Options */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Alternative Options</CardTitle>
          <CardDescription>
            Consider these options before deleting your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">Contact Support</p>
              <p className="text-sm text-muted-foreground">
                If you're experiencing issues, our support team may be able to help.
                Email us at{' '}
                <a href="mailto:streaming@dominioncity.org.za" className="text-primary hover:underline">
                  streaming@dominioncity.org.za
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">Privacy Settings</p>
              <p className="text-sm text-muted-foreground">
                You can adjust your privacy settings or temporarily disable notifications
                instead of deleting your account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
